import { FC, useContext, useMemo, useState } from 'react';

import { H2, Preloader, Tooltip as TooltipKit } from '@smartshell/ui-kit';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LocalizationContext } from 'localization';

import { RevenueTooltip } from '../Revenue/RevenueTooltip';
import { COLORS, COLORS_HOVER } from './constants';
import { IPercentClubLoadProps } from './interfaces';
import s from './PercentClubLoad.module.scss';

export const PercentClubLoad: FC<IPercentClubLoadProps> = ({
  labels,
  values,
  loading,
}) => {
  const { t } = useContext(LocalizationContext);

  const [focusBar, setFocusBar] = useState<number | null>(null);

  const chartData = useMemo(() => {
    if (!labels || !values) return [];

    return values.values.map((value, index) => ({
      name: t('key.load', labels[index]),
      value: value || 0,
    }));
  }, [labels, values]);

  const finalSum = chartData.reduce((currentSum, currentNumber) => {
    return currentSum + currentNumber.value;
  }, 0);

  return (
    <div className={s.container}>
      <div className={s.header}>
        <H2 className={s.title}>{t('visitors.club.load.percent')}</H2>
        <TooltipKit
          contentClassName={s.tooltip}
          content={t('visitors.club.load.text')}
        />
      </div>

      {(labels === undefined || values?.values.every((val) => val === 0)) &&
      !loading ? (
        <div className={s.placeholder}>
          {values?.values.every((val) => val === 0)
            ? t('visitors.empty.data')
            : t('report.placeholder.emptyDates')}
        </div>
      ) : (
        <Preloader loading={loading}>
          <ResponsiveContainer className={s.data} width="100%" height="100%">
            <BarChart
              margin={{ top: 25 }}
              data={chartData}
              onMouseMove={(state) => {
                if (state.isTooltipActive) {
                  setFocusBar(state.activeTooltipIndex ?? null);
                } else {
                  setFocusBar(null);
                }
              }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                dy={8}
                tick={{ fill: 'var(--text-primary)' }}
                fontSize={12}
              />
              <YAxis
                tickFormatter={(value) => `${value} ${t('hour')}`}
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: 'var(--text-primary)',
                  style: { whiteSpace: 'nowrap' },
                }}
                fontSize={12}
              />
              <Tooltip
                cursor={{ fill: 'var(--bg-item-secondary)' }}
                content={({ payload }) => {
                  const rawValue = payload?.[0]?.value;
                  const value = typeof rawValue === 'number' ? rawValue : 0;
                  const label = payload?.[0]?.payload?.name;

                  return (
                    <RevenueTooltip
                      title={String(label)}
                      text_1={t('hours.count')}
                      value_1={`${value} ${t('hour')}`}
                      variant="primary"
                    />
                  );
                }}
              />

              <Bar
                fill="var(--bg-blue-10)"
                dataKey="value"
                radius={[4, 4, 0, 0]}
                isAnimationActive={false}
              >
                <LabelList
                  dataKey="value"
                  content={(props) => {
                    const { x, y, width, value } = props;
                    const XPosition = Number(x) + Number(width) / 2;
                    const YPosition = Number(y) - 8;

                    return (
                      <text
                        x={XPosition}
                        y={YPosition}
                        fill="var(--text-primary)"
                        fontSize="18"
                        textAnchor="middle"
                      >
                        {((Number(value) / finalSum) * 100).toFixed(0)}%
                      </text>
                    );
                  }}
                />
                {chartData?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      focusBar === index
                        ? COLORS_HOVER[index % COLORS.length]
                        : COLORS[index % COLORS.length]
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Preloader>
      )}
    </div>
  );
};
